import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  useBilling,
  PLANS,
  PlanCard,
  UsageDisplay,
  InvoiceList,
  PaymentMethodDisplay,
} from '../features/billing';

export function Billing() {
  const { t } = useTranslation();
  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [changingPlan, setChangingPlan] = useState<string | null>(null);

  const {
    subscription,
    usage,
    invoices,
    paymentMethods,
    isLoading,
    error,
    fetchSubscription,
    fetchUsage,
    fetchInvoices,
    fetchPaymentMethods,
    changePlan,
    cancelSubscription,
    reactivateSubscription,
    createCheckoutSession,
    createPortalSession,
  } = useBilling();

  useEffect(() => {
    fetchSubscription();
    fetchUsage();
    fetchInvoices();
    fetchPaymentMethods();
  }, [fetchSubscription, fetchUsage, fetchInvoices, fetchPaymentMethods]);

  // Check for success/canceled query params from Stripe redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('success') === 'true') {
      setActionMessage({ type: 'success', text: t('billing.paymentSuccess') });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh subscription data
      fetchSubscription();
    } else if (params.get('canceled') === 'true') {
      setActionMessage({ type: 'error', text: t('billing.paymentCanceled') });
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [t, fetchSubscription]);

  const handlePlanSelect = async (planId: 'free' | 'pro' | 'enterprise') => {
    if (!subscription) return;

    // Same plan and not canceled - do nothing
    if (subscription.plan === planId && !subscription.cancelAtPeriodEnd) return;

    setChangingPlan(planId);
    setActionMessage(null);

    try {
      // If reactivating same plan
      if (subscription.plan === planId && subscription.cancelAtPeriodEnd) {
        await reactivateSubscription();
        setActionMessage({ type: 'success', text: t('billing.subscriptionReactivated') });
        return;
      }

      // For upgrades to paid plans without existing Stripe subscription, use checkout
      if (planId !== 'free' && !subscription.stripeSubscriptionId) {
        const url = await createCheckoutSession(planId);
        if (url) {
          window.location.href = url;
          return;
        }
      }

      // For existing subscribers, use API to change plan
      await changePlan(planId);
      setActionMessage({
        type: 'success',
        text:
          planId === 'free'
            ? t('billing.downgradedSuccessfully')
            : t('billing.upgradedSuccessfully'),
      });
    } catch {
      setActionMessage({ type: 'error', text: t('billing.planChangeFailed') });
    } finally {
      setChangingPlan(null);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm(t('billing.confirmCancel'))) return;

    try {
      await cancelSubscription();
      setActionMessage({ type: 'success', text: t('billing.subscriptionCanceled') });
      setTimeout(() => setActionMessage(null), 5000);
    } catch {
      setActionMessage({ type: 'error', text: t('billing.cancelFailed') });
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  const handleManagePaymentMethods = async () => {
    try {
      const url = await createPortalSession();
      if (url) {
        window.location.href = url;
      }
    } catch {
      setActionMessage({ type: 'error', text: t('billing.portalError') });
      setTimeout(() => setActionMessage(null), 5000);
    }
  };

  if (isLoading && !subscription) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const currentPlanIndex = PLANS.findIndex((p) => p.id === subscription?.plan);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('billing.title')}</h1>
        <p className="text-muted-foreground">{t('billing.subtitle')}</p>
      </div>

      {actionMessage && (
        <div
          className={`p-4 rounded-lg ${
            actionMessage.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {actionMessage.text}
        </div>
      )}

      {error && (
        <div className="p-4 rounded-lg bg-red-50 text-red-800 border border-red-200">{error}</div>
      )}

      {/* Subscription Status */}
      {subscription && (
        <Card>
          <CardHeader>
            <CardTitle>{t('billing.currentPlan')}</CardTitle>
            <CardDescription>{t('billing.currentPlanDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold capitalize">{subscription.plan}</p>
                <p className="text-sm text-muted-foreground">
                  {subscription.status === 'trialing' && subscription.trialEnd && (
                    <>
                      {t('billing.trialEnds')}{' '}
                      {new Date(subscription.trialEnd).toLocaleDateString()}
                    </>
                  )}
                  {subscription.status === 'active' && !subscription.cancelAtPeriodEnd && (
                    <>
                      {t('billing.renewsOn')}{' '}
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </>
                  )}
                  {subscription.cancelAtPeriodEnd && (
                    <span className="text-amber-600">
                      {t('billing.cancelsOn')}{' '}
                      {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                {subscription.status === 'past_due' && (
                  <div className="flex items-center gap-2 text-red-600">
                    <AlertCircle className="h-5 w-5" />
                    {t('billing.pastDue')}
                  </div>
                )}
                {subscription.plan !== 'free' &&
                  !subscription.cancelAtPeriodEnd &&
                  subscription.status !== 'canceled' && (
                    <Button variant="outline" onClick={handleCancelSubscription}>
                      {t('billing.cancel')}
                    </Button>
                  )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage and Payment Methods */}
      <div className="grid gap-6 md:grid-cols-2">
        <UsageDisplay usage={usage} />
        <PaymentMethodDisplay
          paymentMethods={paymentMethods}
          onManage={handleManagePaymentMethods}
        />
      </div>

      {/* Invoices */}
      <InvoiceList invoices={invoices} />

      {/* Plans */}
      <Card>
        <CardHeader>
          <CardTitle>{t('billing.availablePlans')}</CardTitle>
          <CardDescription>{t('billing.comparePlans')}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            {PLANS.map((plan, index) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isCurrentPlan={subscription?.plan === plan.id}
                isLoading={changingPlan === plan.id}
                onSelect={() => handlePlanSelect(plan.id)}
                isUpgrade={index > currentPlanIndex}
                isCanceled={subscription?.cancelAtPeriodEnd && subscription?.plan === plan.id}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
